import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaEye, FaMoneyBill, FaBan, FaSearch, FaPrint, FaFileExcel, FaEdit } from 'react-icons/fa';
import { getNotesReceivable, clearError, clearSuccess, getBanks, collectNoteReceivable, cancelNoteReceivable, getCustomers } from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import {
  generatePdfFromElementIds,
  generateSingleElementPdf,
  waitForRender,
} from '../../../../shared/pdf/pdfEngine';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { calculatePdfPagination, convertArabicNumbersToEnglish } from '../../../../shared/utils.js/utils';


// notes receivable component
const NotesReceivable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { notesReceivable, notesReceivablePagination, isLoading, error, success, banks, banksPagination, customers, customersPagination } = useSelector((s) => s.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    customer_id: '',
    date_between_start: '',
    date_between_end: '',
    due_between_start: '',
    due_between_end: ''
  });
  const searchTimeoutRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [collectId, setCollectId] = useState(null);
  const [collectForm, setCollectForm] = useState({ bank_account_id: '', amount: '', collection_date: '' });
  const [cancelId, setCancelId] = useState(null);
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
  const [bankSearchTerm, setBankSearchTerm] = useState('');
  const [bankCurrentPage, setBankCurrentPage] = useState(1);
  const bankSearchInputRef = useRef(null);
  const bankSearchTimeoutRef = useRef(null);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [customerCurrentPage, setCustomerCurrentPage] = useState(1);
  const customerSearchInputRef = useRef(null);
  const customerSearchTimeoutRef = useRef(null);
  const [noteForPrint, setNoteForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null); // 'single' or 'all'
  const [previewNote, setPreviewNote] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { t, i18n: i18nHook } = useTranslation('global');
  const isRTL = i18nHook.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;
  const [allNotesForPDF, setAllNotesForPDF] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);

  useEffect(() => {
    setTitle('sidenav.notesReceivable22');
  }, [setTitle]);

  // Close dropdowns when clicking outside or when opening another dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInsideDropdown = event.target.closest('[data-dropdown-container]');
      const isClickOnDropdownToggle = event.target.closest('[data-dropdown-toggle]');

      if (!isClickInsideDropdown && !isClickOnDropdownToggle) {
        setBankDropdownOpen(false);
        setCustomerDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close other dropdowns when opening a new one
  useEffect(() => {
    if (bankDropdownOpen) {
      setCustomerDropdownOpen(false);
    }
  }, [bankDropdownOpen]);

  useEffect(() => {
    if (customerDropdownOpen) {
      setBankDropdownOpen(false);
    }
  }, [customerDropdownOpen]);

  // Load customers for filter dropdown
  useEffect(() => {
    if (customerDropdownOpen) {
      dispatch(getCustomers({ page: customerCurrentPage, per_page: 10, search: customerSearchTerm || undefined }));
    }
  }, [dispatch, customerCurrentPage, customerDropdownOpen]);

  // Search customers with debounce
  useEffect(() => {
    if (customerSearchTerm !== undefined && customerDropdownOpen) {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
      customerSearchTimeoutRef.current = setTimeout(() => {
        setCustomerCurrentPage(1);
        dispatch(getCustomers({ page: 1, per_page: 10, search: customerSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (customerSearchTimeoutRef.current) clearTimeout(customerSearchTimeoutRef.current);
    };
  }, [customerSearchTerm, customerDropdownOpen, dispatch]);

  // Load banks for collect modal with pagination
  useEffect(() => {
    if (bankDropdownOpen) {
      dispatch(getBanks({ page: bankCurrentPage, per_page: 10, search: bankSearchTerm || undefined }));
    }
  }, [dispatch, bankCurrentPage, bankDropdownOpen]);

  // Search banks with debounce
  useEffect(() => {
    if (bankSearchTerm !== undefined && bankDropdownOpen) {
      if (bankSearchTimeoutRef.current) clearTimeout(bankSearchTimeoutRef.current);
      bankSearchTimeoutRef.current = setTimeout(() => {
        setBankCurrentPage(1);
        dispatch(getBanks({ page: 1, per_page: 10, search: bankSearchTerm || undefined }));
      }, 300);
    }
    return () => {
      if (bankSearchTimeoutRef.current) clearTimeout(bankSearchTimeoutRef.current);
    };
  }, [bankSearchTerm, bankDropdownOpen, dispatch]);

  useEffect(() => {
    const params = { page: currentPage, per_page: perPage };
    if (searchTerm) params.search = searchTerm;
    if (filters.status) params.status = filters.status;
    if (filters.customer_id) params.customer_id = filters.customer_id;
    if (filters.date_between_start && filters.date_between_end) {
      params.date_between = `${filters.date_between_start},${filters.date_between_end}`;
    }
    if (filters.due_between_start && filters.due_between_end) {
      params.due_between = `${filters.due_between_start},${filters.due_between_end}`;
    }
    dispatch(getNotesReceivable(params));
  }, [dispatch, currentPage, searchTerm, filters, perPage]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch]);

  const pagination = notesReceivablePagination || { current_page: 1, last_page: 1 };
  const items = Array.isArray(notesReceivable) ? notesReceivable : [];

  const displayBanks = Array.isArray(banks) ? banks : [];
  const bankPagination = banksPagination || { current_page: 1, last_page: 1 };
  const displayCustomers = Array.isArray(customers) ? customers : [];
  const customerPagination = customersPagination || { current_page: 1, last_page: 1 };

  useEffect(() => {
    if (bankDropdownOpen && bankSearchInputRef.current) {
      bankSearchInputRef.current.focus();
    }
  }, [bankDropdownOpen]);

  useEffect(() => {
    if (customerDropdownOpen && customerSearchInputRef.current) {
      customerSearchInputRef.current.focus();
    }
  }, [customerDropdownOpen]);

  const resetFilters = () => {
    setFilters({
      status: '',
      customer_id: '',
      date_between_start: '',
      date_between_end: '',
      due_between_start: '',
      due_between_end: ''
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return convertArabicNumbersToEnglish(date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' }));
    } catch {
      return convertArabicNumbersToEnglish(dateString?.split('T')[0] || dateString || '—');
    }
  };

  const formatCurrency = (amount) => {
    if (amount == null || amount === '') return '—';
    const num = Number(amount);
    if (isNaN(num)) return convertArabicNumbersToEnglish(amount);
    return convertArabicNumbersToEnglish(num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  const getStatusText = (status) => {
    const s = String(status || '').toLowerCase();
    const map = {
      collected: t('notesReceivable.status.collected', { defaultValue: 'محصل' }),
      under_collection: t('notesReceivable.status.under_collection', { defaultValue: 'تحت التحصيل' }),
      canceled: t('notesReceivable.status.canceled', { defaultValue: 'ملغي' }),
    };
    return map[s] || status || '—';
  };

  const handlePerPageInputChange = (value) => {
    const parsed = Number(value);
    const total = notesReceivablePagination?.total;
    const maxAllowed = Number.isFinite(total) && total > 0 ? total : 1000;
    const next = Math.min(Math.max(1, Number.isNaN(parsed) ? 1 : Math.round(parsed)), maxAllowed);
    setPerPageInput(next);
  };

  const handleApplyPerPage = () => {
    const total = notesReceivablePagination?.total;
    const maxAllowed = Number.isFinite(total) && total > 0 ? total : 1000;
    const next = Math.min(Math.max(1, Number(perPageInput) || 1), maxAllowed);
    setPerPage(next);
    setPerPageInput(next);
    setCurrentPage(1);
  };

  // Handle print single note receivable
  const handlePrintNoteReceivable = async (note) => {
    try {
      setNoteForPrint(note);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`note-receivable-pdf-${note.id}`);
      if (!element) {
        toast.error(t('notesReceivable.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        return;
      }

      // Shared PDF Engine — capture + assembly
      const identifier = note.note_number || note.id;
      await generateSingleElementPdf({
        elementId: element.id,
        fileName: `ACE_NoteReceivable_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
    } catch (err) {
      console.error('Note Receivable PDF error:', err);
      toast.error(t('notesReceivable.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setNoteForPrint(null);
      setPdfType(null);
    }
  };

  // Handle preview single note receivable
  const handlePreviewSingle = (note) => {
    if (!note) {
      toast.error(t('notesReceivable.print.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setPreviewNote(note);
    setIsPreviewOpen(true);
  };

  const buildDynamicPdfChunks = (notes) => {
    const topHeader = document.getElementById('notes-receivable-pdf-measure-top-header');
    const thead = document.getElementById('notes-receivable-pdf-measure-thead');
    const footer = document.getElementById('notes-receivable-pdf-measure-footer');
    const tbody = document.getElementById('notes-receivable-pdf-measure-tbody');
    return calculatePdfPagination(notes, { topHeader, thead, footer, tbody });
  };

  // Handle preview all notes receivable
  const handlePreviewAll = async () => {
    if (!items || items.length === 0) {
      toast.error(t('notesReceivable.preview.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }

    // Fetch all notes for preview
    try {
      const params = { per_page: 1000 };
      if (searchTerm) params.search = searchTerm;
      if (filters.status) params.status = filters.status;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.date_between_start && filters.date_between_end) {
        params.date_between = `${filters.date_between_start},${filters.date_between_end}`;
      }
      if (filters.due_between_start && filters.due_between_end) {
        params.due_between = `${filters.due_between_start},${filters.due_between_end}`;
      }
      const result = await dispatch(getNotesReceivable(params));
      if (result.payload?.data?.data) {
        const allNotes = Array.isArray(result.payload.data.data) ? result.payload.data.data : [];
        const chunks = buildDynamicPdfChunks(allNotes);
        setAllNotesForPDF(allNotes);
        setPdfChunks(chunks);
        setPreviewNote({ all: true });
        setIsPreviewOpen(true);
      } else {
        const chunks = buildDynamicPdfChunks(items);
        setAllNotesForPDF(items);
        setPdfChunks(chunks);
        setPreviewNote({ all: true });
        setIsPreviewOpen(true);
      }
    } catch (error) {
      const chunks = buildDynamicPdfChunks(items);
      setAllNotesForPDF(items);
      setPdfChunks(chunks);
      setPreviewNote({ all: true });
      setIsPreviewOpen(true);
    }
  };

  // Preload image helper
  const preloadImage = (src) =>
    new Promise((resolve) => {
      if (!src) return resolve();
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = src;
    });

  // Handle print all notes receivable
  const handlePrintAll = async () => {
    const notesToPrint = allNotesForPDF.length > 0 ? allNotesForPDF : items;
    if (!notesToPrint || notesToPrint.length === 0) {
      toast.error(t('notesReceivable.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      // Fetch all notes if not already loaded
      if (allNotesForPDF.length === 0) {
        const params = { per_page: 1000 };
        if (searchTerm) params.search = searchTerm;
        if (filters.status) params.status = filters.status;
        if (filters.customer_id) params.customer_id = filters.customer_id;
        if (filters.date_between_start && filters.date_between_end) {
          params.date_between = `${filters.date_between_start},${filters.date_between_end}`;
        }
        if (filters.due_between_start && filters.due_between_end) {
          params.due_between = `${filters.due_between_start},${filters.due_between_end}`;
        }
        const result = await dispatch(getNotesReceivable(params));
        if (result.payload?.data?.data) {
          const allNotes = Array.isArray(result.payload.data.data) ? result.payload.data.data : [];
          setAllNotesForPDF(allNotes);
        }
      }

      const finalNotes = allNotesForPDF.length > 0 ? allNotesForPDF : notesToPrint;
      const chunks = buildDynamicPdfChunks(finalNotes);
      setPdfChunks(chunks);
      setPdfType('all');

      await preloadImage(`${publicUrl}/HeaderAce.png`);
      await preloadImage(`${publicUrl}/FooterAce.png`);
      await preloadImage(headerLogo);
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIsGeneratingPdf(true);

      await waitForRender(300);

      // Shared PDF Engine — capture + assembly (one A4 page per chunk)
      const pageElementIds = chunks.map((_, i) => `notes-receivable-pdf-all-${i}`);
      await generatePdfFromElementIds({
        elementIds: pageElementIds,
        fileName: `ACE_NotesReceivable_${new Date().toISOString().split('T')[0]}.pdf`,
        isRTL,
      });
      toast.success(t('notesReceivable.print.success', { defaultValue: 'تم إنشاء PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Notes Receivable PDF error:', error);
      toast.error(t('notesReceivable.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف أوراق القبض' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  // Handle export single note receivable to Excel
  const handleExportNoteReceivableToExcel = (note) => {
    const data = [{
      [t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })]: note.customer_name || note.customer?.name_ar || note.customer?.name || '—',
      [t('notesReceivable.tableHeaders.project', { defaultValue: 'المشروع' })]: note.project_name || note.project?.name_ar || note.project?.name || '—',
      [t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })]: note.note_number || '—',
      [t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })]: formatCurrency(note.amount) || '—',
      [t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })]: formatDate(note.issue_date) || '—',
      [t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })]: formatDate(note.due_date) || '—',
      [t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })]: getStatusText(note.status) || '—'
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('notesReceivable.noteReceivable', { defaultValue: 'Note Receivable' }));
    const identifier = note.note_number || note.id;
    const fileName = `${isRTL ? 'ورقة_قبض' : 'NoteReceivable'}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('notesReceivable.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  // Handle export all notes receivable to Excel
  const handleExportAllToExcel = () => {
    if (!items || items.length === 0) {
      toast.error(t('notesReceivable.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    const data = items.map((note, index) => ({
      'م': ((pagination?.current_page || 1) - 1) * perPage + (index + 1),
      [t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })]: note.customer_name || note.customer?.name_ar || note.customer?.name || '—',
      [t('notesReceivable.tableHeaders.project', { defaultValue: 'المشروع' })]: note.project_name || note.project?.name_ar || note.project?.name || '—',
      [t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })]: note.note_number || '—',
      [t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })]: formatCurrency(note.amount) || '—',
      [t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })]: formatDate(note.issue_date) || '—',
      [t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })]: formatDate(note.due_date) || '—',
      [t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })]: getStatusText(note.status) || '—'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('notesReceivable.notesReceivable', { defaultValue: 'Notes Receivable' }));
    const fileName = `${isRTL ? 'أوراق_القبض' : 'NotesReceivable'}_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(t('notesReceivable.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: '20px', textAlign: textAlign }}>{t('notesReceivable.title', { defaultValue: 'أوراق القبض' })}</h1>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={handlePreviewAll}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              direction: dir,
              textAlign: textAlign
            }}
          >
            <FaEye /> {t('notesReceivable.preview.all', { defaultValue: 'عرض PDF للكل' })}
          </button>
          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: isGeneratingPdf ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              opacity: isGeneratingPdf ? 0.6 : 1,
              direction: dir,
              textAlign: textAlign
            }}
          >
            <FaPrint /> {isGeneratingPdf ? t('notesReceivable.print.generating', { defaultValue: 'جاري الطباعة...' }) : t('notesReceivable.print.all', { defaultValue: 'طباعة PDF للكل' })}
          </button>
          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              direction: dir,
              textAlign: textAlign
            }}
          >
            <FaFileExcel /> {t('notesReceivable.export.all', { defaultValue: 'تصدير Excel الكل' })}
          </button>
          <button
            onClick={() => navigate('/notes-receivable/add')}
            style={{
              backgroundColor: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              direction: dir,
              textAlign: textAlign
            }}
          >
            <FaPlus /> {t('notesReceivable.add', { defaultValue: 'إضافة ورقة قبض' })}
          </button>
        </div>
      </div>

      {/* Filters Section */}
      <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ color: '#ccc', fontSize: 13, direction: dir, textAlign: textAlign }}>{t('common.perPage')}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, padding: '6px 8px' }}>
            <button
              type="button"
              onClick={() => handlePerPageInputChange(Math.max(1, (Number(perPageInput) || 1) - 1))}
              style={{ background: '#2a2f3d', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: perPageInput <= 1 ? 'not-allowed' : 'pointer' }}
              disabled={perPageInput <= 1}
              title={t('notesReceivable.pagination.decrease', { defaultValue: 'تقليل' })}
            >
              -
            </button>
            <input
              type="number"
              min="1"
              value={perPageInput}
              onChange={(e) => handlePerPageInputChange(e.target.value)}
              style={{ width: 70, padding: '6px 8px', background: '#14263b', border: '1px solid #333', borderRadius: 6, color: 'white', textAlign: 'center' }}
            />
            <button
              type="button"
              onClick={() => handlePerPageInputChange((Number(perPageInput) || 1) + 1)}
              style={{ background: '#2a2f3d', color: 'white', border: 'none', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
              title={t('notesReceivable.pagination.increase', { defaultValue: 'زيادة' })}
            >
              +
            </button>
            <button
              type="button"
              onClick={handleApplyPerPage}
              style={{ background: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              {t('common.apply', { defaultValue: 'تطبيق' })}
            </button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 12 }}>
          {/* Search by code or customer name */}
          <div style={{ position: 'relative', gridColumn: '1 / -1', minWidth: '100%' }}>
            <FaSearch style={{
              position: 'absolute',
              [isRTL ? 'left' : 'right']: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontSize: '16px',
              zIndex: 1,
              pointerEvents: 'none'
            }} />
            <input
              type="text"
              placeholder={t('notesReceivable.filters.searchPlaceholder', { defaultValue: 'ابحث بالكود أو اسم العميل' })}
              value={searchTerm}
              onChange={(e) => {
                const val = e.target.value;
                setSearchTerm(val);
                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = setTimeout(() => {
                  setCurrentPage(1);
                }, 300);
              }}
              style={{
                width: '100%',
                padding: isRTL ? '12px 15px 12px 45px' : '12px 45px 12px 15px',
                direction: dir,
                textAlign: textAlign,
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                boxSizing: 'border-box'
              }}
            />
          </div>

          {/* Customer Filter */}
          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#ccc', textAlign: textAlign }}>{t('notesReceivable.filters.customer', { defaultValue: 'العميل' })}</label>
            <div
              data-dropdown-toggle
              onClick={() => {
                if (customerDropdownOpen) {
                  setCustomerSearchTerm('');
                  setCustomerCurrentPage(1);
                }
                setCustomerDropdownOpen((p) => !p);
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: '#1a1f2e',
                color: 'white',
                border: '1px solid #333',
                borderRadius: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: 14
              }}
            >
              <span>
                {filters.customer_id
                  ? (() => {
                    const c = displayCustomers.find((x) => String(x.id) === String(filters.customer_id));
                    return c ? `${c.code ? `${c.code} - ` : ''}${isRTL ? (c.name_ar || c.name || '') : (c.name_en || c.name_ar || c.name || '')}` : t('notesReceivable.filters.selectCustomer', { defaultValue: 'اختر العميل' });
                  })()
                  : t('notesReceivable.filters.allCustomers', { defaultValue: 'جميع العملاء' })}
              </span>
              <span style={{ fontSize: 12 }}>▼</span>
            </div>
            {customerDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  zIndex: 1000,
                  top: '100%',
                  left: 0,
                  right: 0,
                  background: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  marginTop: 4,
                  maxHeight: 260,
                  overflowY: 'auto',
                }}
              >
                <div style={{ padding: 8 }}>
                  <input
                    ref={customerSearchInputRef}
                    type="text"
                    placeholder={t('notesReceivable.filters.searchCustomer', { defaultValue: 'ابحث عن عميل' })}
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: 8,
                      backgroundColor: '#14263b',
                      border: '1px solid #444',
                      borderRadius: 6,
                      color: 'white',
                      fontSize: 14,
                    }}
                  />
                </div>
                {customerPagination && customerPagination.last_page >= 1 && (
                  <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign: textAlign }}>{t('notesReceivable.dropdown.page', { defaultValue: 'الصفحة' })} {customerPagination.current_page} {t('notesReceivable.dropdown.of', { defaultValue: 'من' })} {customerPagination.last_page}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (customerPagination.current_page > 1) setCustomerCurrentPage(1);
                        }}
                        disabled={customerPagination.current_page === 1}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: customerPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                          color: customerPagination.current_page === 1 ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: customerPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                      >
                        ««
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (customerPagination.current_page > 1) setCustomerCurrentPage(customerPagination.current_page - 1);
                        }}
                        disabled={customerPagination.current_page === 1}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: customerPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                          color: customerPagination.current_page === 1 ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: customerPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                      >
                        ‹
                      </button>
                      {(() => {
                        const pages = [];
                        const current = customerPagination.current_page;
                        const last = customerPagination.last_page;
                        const showPages = 5;
                        let start = Math.max(1, current - Math.floor(showPages / 2));
                        let end = Math.min(last, start + showPages - 1);
                        if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
                        for (let i = start; i <= end; i++) pages.push(i);
                        return pages.map((page) => (
                          <button
                            key={page}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCustomerCurrentPage(page);
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                              color: 'white',
                              border: page === current ? '1px solid #AC2000' : '1px solid #444',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: 10,
                              fontWeight: page === current ? 'bold' : 'normal',
                              minWidth: '28px',
                            }}
                          >
                            {page}
                          </button>
                        ));
                      })()}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (customerPagination.current_page < customerPagination.last_page) setCustomerCurrentPage(customerPagination.current_page + 1);
                        }}
                        disabled={customerPagination.current_page === customerPagination.last_page}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: customerPagination.current_page === customerPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                          color: customerPagination.current_page === customerPagination.last_page ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: customerPagination.current_page === customerPagination.last_page ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                      >
                        ›
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (customerPagination.current_page < customerPagination.last_page) setCustomerCurrentPage(customerPagination.last_page);
                        }}
                        disabled={customerPagination.current_page === customerPagination.last_page}
                        style={{
                          padding: '4px 8px',
                          backgroundColor: customerPagination.current_page === customerPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                          color: customerPagination.current_page === customerPagination.last_page ? '#666' : 'white',
                          border: '1px solid #444',
                          borderRadius: '4px',
                          cursor: customerPagination.current_page === customerPagination.last_page ? 'not-allowed' : 'pointer',
                          fontSize: 10,
                          fontWeight: 'bold',
                          minWidth: '28px',
                        }}
                      >
                        »»
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <div
                    onClick={() => {
                      setFilters((prev) => ({ ...prev, customer_id: '' }));
                      setCustomerDropdownOpen(false);
                      setCustomerSearchTerm('');
                      setCustomerCurrentPage(1);
                    }}
                    style={{
                      padding: 10,
                      cursor: 'pointer',
                      borderTop: '1px solid #333',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      direction: dir,
                      textAlign: textAlign
                    }}
                  >
                    <span>{t('notesReceivable.filters.allCustomers', { defaultValue: 'جميع العملاء' })}</span>
                  </div>
                  {displayCustomers.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => {
                        setFilters((prev) => ({ ...prev, customer_id: c.id }));
                        setCustomerDropdownOpen(false);
                        setCustomerSearchTerm('');
                        setCustomerCurrentPage(1);
                      }}
                      style={{
                        padding: 10,
                        cursor: 'pointer',
                        borderTop: '1px solid #333',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        direction: dir,
                        textAlign: textAlign
                      }}
                    >
                      <span>{`${c.code ? `${c.code} - ` : ''}${isRTL ? (c.name_ar || c.name || '') : (c.name_en || c.name_ar || c.name || '')}`}</span>
                    </div>
                  ))}
                  {displayCustomers.length === 0 && (
                    <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: dir }}>{t('notesReceivable.filters.noResults', { defaultValue: 'لا توجد نتائج' })}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#ccc', textAlign: textAlign }}>{t('notesReceivable.filters.status', { defaultValue: 'الحالة' })}</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                direction: dir,
                textAlign: textAlign
              }}
            >
              <option value="">{t('notesReceivable.filters.allStatuses', { defaultValue: 'جميع الحالات' })}</option>
              <option value="collected">{t('notesReceivable.status.collected', { defaultValue: 'محصل' })}</option>
              <option value="under_collection">{t('notesReceivable.status.under_collection', { defaultValue: 'تحت التحصيل' })}</option>
              <option value="canceled">{t('notesReceivable.status.canceled', { defaultValue: 'ملغي' })}</option>
            </select>
          </div>

          {/* Date Between Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#ccc', textAlign: textAlign }}>{t('notesReceivable.filters.issueDateFrom', { defaultValue: 'تاريخ الإصدار من' })}</label>
            <input
              type="date"
              value={filters.date_between_start}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_between_start: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#ccc', textAlign: textAlign }}>{t('notesReceivable.filters.issueDateTo', { defaultValue: 'تاريخ الإصدار إلى' })}</label>
            <input
              type="date"
              value={filters.date_between_end}
              onChange={(e) => setFilters((prev) => ({ ...prev, date_between_end: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14
              }}
            />
          </div>

          {/* Due Between Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#ccc', textAlign: textAlign }}>{t('notesReceivable.filters.dueDateFrom', { defaultValue: 'تاريخ الاستحقاق من' })}</label>
            <input
              type="date"
              value={filters.due_between_start}
              onChange={(e) => setFilters((prev) => ({ ...prev, due_between_start: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 12, color: '#ccc', textAlign: textAlign }}>{t('notesReceivable.filters.dueDateTo', { defaultValue: 'تاريخ الاستحقاق إلى' })}</label>
            <input
              type="date"
              value={filters.due_between_end}
              onChange={(e) => setFilters((prev) => ({ ...prev, due_between_end: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: 8,
                color: 'white',
                fontSize: 14
              }}
            />
          </div>
        </div>

        {/* Reset Filters Button */}
        {(filters.status || filters.customer_id || filters.date_between_start || filters.date_between_end || filters.due_between_start || filters.due_between_end || searchTerm) && (
          <button
            onClick={resetFilters}
            style={{
              padding: '8px 16px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
              marginTop: 8
            }}
          >
            {t('notesReceivable.filters.resetFilters', { defaultValue: 'إعادة تعيين الفلاتر' })}
          </button>
        )}
      </div>

      <div style={{ overflowX: 'auto', background: '#1b3350', border: '1px solid #333', borderRadius: 12 }}>
        {/* Helper to format ISO datetime to date */}
        {/*
          Keep inline helper minimal to avoid imports; shows yyyy-mm-dd.
        */}
        {null}
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px', tableLayout: 'auto' }}>
          <thead>
            <tr style={{ backgroundColor: '#AC2000' }}>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.project', { defaultValue: 'Project' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white' }}>{t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
              <th style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', color: 'white', minWidth: '400px' }}>{t('notesReceivable.tableHeaders.actions', { defaultValue: 'الإجراءات' })}</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: 20, direction: dir }}>{t('notesReceivable.noData', { defaultValue: 'لا توجد بيانات' })}</td>
              </tr>
            )}
            {items.map((nr) => (
              <tr key={nr.id}>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: textAlign, whiteSpace: 'nowrap', direction: dir }}>
                  {nr.customer_name || (isRTL ? (nr.customer?.name_ar || nr.customer?.name || '') : (nr.customer?.name_en || nr.customer?.name_ar || nr.customer?.name || '')) || '—'}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: textAlign, whiteSpace: 'nowrap', direction: dir }}>
                  {nr.project_name || (isRTL ? (nr.project?.name_ar || nr.project?.name || '') : (nr.project?.name_en || nr.project?.name_ar || nr.project?.name || '')) || '—'}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {nr.note_number || '—'}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: textAlign, whiteSpace: 'nowrap', direction: dir }}>
                  {formatCurrency(nr.amount)}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {nr.issue_date ? String(nr.issue_date).split('T')[0] : '—'}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {nr.due_date ? String(nr.due_date).split('T')[0] : '—'}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center' }}>
                  {(() => {
                    const s = String(nr.status || '').toLowerCase();
                    const map = {
                      collected: { text: t('notesReceivable.status.collected', { defaultValue: 'محصل' }), bg: '#198754' },
                      under_collection: { text: t('notesReceivable.status.under_collection', { defaultValue: 'تحت التحصيل' }), bg: '#ffc107', color: '#000' },
                      canceled: { text: t('notesReceivable.status.canceled', { defaultValue: 'ملغي' }), bg: '#dc3545' },
                    };
                    const meta = map[s] || { text: nr.status || '—', bg: '#6c757d' };
                    return (
                      <span style={{ backgroundColor: meta.bg, color: meta.color || 'white', padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: 'bold', display: 'inline-block', whiteSpace: 'nowrap', direction: dir }}>
                        {meta.text}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ padding: 12, borderBottom: '1px solid #333', textAlign: 'center', minWidth: '400px' }}>
                  <div style={{ display: 'inline-flex', gap: 8, flexWrap: 'nowrap', justifyContent: 'center' }}>
                    <button
                      onClick={() => handlePreviewSingle(nr)}
                      style={{ background: '#444', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 14, direction: dir, textAlign: textAlign }}
                      title={t('notesReceivable.actions.view', { defaultValue: 'عرض' })}
                    >
                      <FaEye /> {t('notesReceivable.actions.view', { defaultValue: 'عرض' })}
                    </button>
                    {(String(nr.status).toLowerCase() === 'under_collection' || String(nr.status).toLowerCase() === 'pending' || String(nr.status).toLowerCase() === 'unpaid' || !nr.status) && (
                      <button
                        onClick={() => navigate(`/notes-receivable/edit/${nr.id}`)}
                        style={{ background: '#ffc107', color: '#000', border: 'none', borderRadius: 6, padding: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                        title={t('common.edit', { defaultValue: 'تعديل' })}
                      >
                        <FaEdit /> {t('common.edit', { defaultValue: 'تعديل' })}


                      </button>
                    )}
                    <button
                      onClick={() => handlePrintNoteReceivable(nr)}
                      disabled={isGeneratingPdf}
                      style={{ background: isGeneratingPdf ? '#666' : '#17a2b8', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: isGeneratingPdf ? 0.6 : 1, whiteSpace: 'nowrap', fontSize: 14, direction: dir, textAlign: textAlign }}
                      title={t('notesReceivable.print.single', { defaultValue: 'طباعة' })}
                    >
                      <FaPrint /> {t('notesReceivable.print.single', { defaultValue: 'طباعة' })}
                    </button>
                    <button
                      onClick={() => handleExportNoteReceivableToExcel(nr)}
                      style={{ background: '#28a745', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 14, direction: dir, textAlign: textAlign }}
                      title={t('notesReceivable.export.single', { defaultValue: 'تصدير Excel' })}
                    >
                      <FaFileExcel /> {t('notesReceivable.export.single', { defaultValue: 'تصدير Excel' })}
                    </button>
                    {String(nr.status || '').toLowerCase() === 'under_collection' && (
                      <button
                        onClick={() => { setCollectId(nr.id); setCollectForm({ bank_account_id: '', amount: nr?.amount ?? '', collection_date: '' }); }}
                        style={{ background: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 14, direction: dir, textAlign: textAlign }}
                        title={t('notesReceivable.actions.collect', { defaultValue: 'تحصيل' })}
                      >
                        <FaMoneyBill /> {t('notesReceivable.actions.collect', { defaultValue: 'تحصيل' })}
                      </button>
                    )}
                    {String(nr.status || '').toLowerCase() === 'under_collection' && (
                      <button
                        onClick={() => setCancelId(nr.id)}
                        style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', fontSize: 14, direction: dir, textAlign: textAlign }}
                        title={t('notesReceivable.actions.cancel', { defaultValue: 'إلغاء' })}
                      >
                        <FaBan /> {t('notesReceivable.actions.cancel', { defaultValue: 'إلغاء' })}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination (Vendors style) */}
      {pagination && (() => {
        const current_page = pagination.current_page || 1;
        const last_page = pagination.last_page || 1;
        const pages = [];
        for (let i = 1; i <= last_page; i++) {
          if (i === 1 || i === last_page || (i >= current_page - 2 && i <= current_page + 2)) {
            pages.push(i);
          } else if (i === current_page - 3 || i === current_page + 3) {
            pages.push('...');
          }
        }
        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '20px', direction: dir }}>
            <button
              onClick={() => setCurrentPage(current_page - 1)}
              disabled={current_page === 1}
              style={{
                padding: '8px 12px',
                backgroundColor: current_page === 1 ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: current_page === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              {t('notesReceivable.pagination.previous', { defaultValue: 'السابق' })}
            </button>
            {pages.map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} style={{ padding: '8px', color: 'white' }}>...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: page === current_page ? '#AC2000' : '#1a1f2e',
                    color: 'white',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage(current_page + 1)}
              disabled={current_page === last_page}
              style={{
                padding: '8px 12px',
                backgroundColor: current_page === last_page ? '#333' : '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: current_page === last_page ? 'not-allowed' : 'pointer'
              }}
            >
              {t('notesReceivable.pagination.next', { defaultValue: 'التالي' })}
            </button>
          </div>
        );
      })()}

      {collectId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 20, width: 420, maxWidth: '95%' }}>
            <h3 style={{ color: 'white', margin: '0 0 12px', fontSize: '18px', direction: dir, textAlign: textAlign }}>{t('notesReceivable.collect.title', { defaultValue: 'تحصيل ورقة قبض' })}</h3>
            <div style={{ marginBottom: 12, position: 'relative' }} data-dropdown-container>
              <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 6, direction: dir, textAlign: textAlign }}>{t('notesReceivable.collect.bankAccount', { defaultValue: 'الحساب البنكي' })}</label>
              <div
                data-dropdown-toggle
                onClick={() => {
                  if (bankDropdownOpen) {
                    setBankSearchTerm('');
                    setBankCurrentPage(1);
                  }
                  setBankDropdownOpen((p) => !p);
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#1a1f2e',
                  color: 'white',
                  border: '1px solid #333',
                  borderRadius: 6,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <span>
                  {collectForm.bank_account_id
                    ? (() => {
                      const b = (banks || []).find((x) => String(x.id) === String(collectForm.bank_account_id));
                      return b ? `${b.code ? `${b.code} - ` : ''}${isRTL ? (b.name_ar || b.name || '') : (b.name_en || b.name_ar || b.name || '')}` : t('notesReceivable.collect.selectBankAccount', { defaultValue: 'اختر الحساب' });
                    })()
                    : t('notesReceivable.collect.selectBankAccount', { defaultValue: 'اختر الحساب' })}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              {bankDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    zIndex: 10,
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: 6,
                    marginTop: 4,
                    maxHeight: 260,
                    overflowY: 'auto',
                  }}
                >
                  <div style={{ padding: 8 }}>
                    <input
                      ref={bankSearchInputRef}
                      type="text"
                      placeholder={t('notesReceivable.collect.searchBankAccount', { defaultValue: 'ابحث عن حساب بنكي' })}
                      value={bankSearchTerm}
                      onChange={(e) => setBankSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        width: '100%',
                        padding: 8,
                        backgroundColor: '#14263b',
                        border: '1px solid #444',
                        borderRadius: 6,
                        color: 'white',
                        fontSize: 14,
                      }}
                    />
                  </div>
                  {bankPagination && bankPagination.last_page >= 1 && (
                    <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign: textAlign }}>{t('notesReceivable.dropdown.page', { defaultValue: 'الصفحة' })} {bankPagination.current_page} {t('notesReceivable.dropdown.of', { defaultValue: 'من' })} {bankPagination.last_page}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (bankPagination.current_page > 1) {
                              setBankCurrentPage(1);
                            }
                          }}
                          disabled={bankPagination.current_page === 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: bankPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                            color: bankPagination.current_page === 1 ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: bankPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('notesReceivable.dropdown.firstPage', { defaultValue: 'الصفحة الأولى' })}
                        >
                          ««
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (bankPagination.current_page > 1) {
                              setBankCurrentPage(bankPagination.current_page - 1);
                            }
                          }}
                          disabled={bankPagination.current_page === 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: bankPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                            color: bankPagination.current_page === 1 ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: bankPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('notesReceivable.pagination.previous', { defaultValue: 'السابق' })}
                        >
                          ‹
                        </button>
                        {(() => {
                          const pages = [];
                          const current = bankPagination.current_page;
                          const last = bankPagination.last_page;
                          const showPages = 5;
                          let start = Math.max(1, current - Math.floor(showPages / 2));
                          let end = Math.min(last, start + showPages - 1);
                          if (end - start < showPages - 1) {
                            start = Math.max(1, end - showPages + 1);
                          }
                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }
                          return pages.map((page) => (
                            <button
                              key={page}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setBankCurrentPage(page);
                              }}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                color: 'white',
                                border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 10,
                                fontWeight: page === current ? 'bold' : 'normal',
                                minWidth: '28px',
                              }}
                            >
                              {page}
                            </button>
                          ));
                        })()}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (bankPagination.current_page < bankPagination.last_page) {
                              setBankCurrentPage(bankPagination.current_page + 1);
                            }
                          }}
                          disabled={bankPagination.current_page === bankPagination.last_page}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: bankPagination.current_page === bankPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                            color: bankPagination.current_page === bankPagination.last_page ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: bankPagination.current_page === bankPagination.last_page ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('notesReceivable.pagination.next', { defaultValue: 'التالي' })}
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (bankPagination.current_page < bankPagination.last_page) {
                              setBankCurrentPage(bankPagination.last_page);
                            }
                          }}
                          disabled={bankPagination.current_page === bankPagination.last_page}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: bankPagination.current_page === bankPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                            color: bankPagination.current_page === bankPagination.last_page ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: bankPagination.current_page === bankPagination.last_page ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                          title={t('notesReceivable.dropdown.lastPage', { defaultValue: 'الصفحة الأخيرة' })}
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    {displayBanks.map((b) => (
                      <div
                        key={b.id}
                        onClick={() => {
                          setCollectForm({ ...collectForm, bank_account_id: b.id });
                          setBankDropdownOpen(false);
                          setBankSearchTerm('');
                          setBankCurrentPage(1);
                        }}
                        style={{
                          padding: 10,
                          cursor: 'pointer',
                          borderTop: '1px solid #333',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>{`${b.code ? `${b.code} - ` : ''}${b.name_ar || b.name || ''}`}</span>
                      </div>
                    ))}
                    {displayBanks.length === 0 && (
                      <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: dir }}>{t('notesReceivable.collect.noBanks', { defaultValue: 'لا توجد بنوك' })}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 6, direction: dir, textAlign: textAlign }}>{t('notesReceivable.collect.amount', { defaultValue: 'قيمة التحصيل' })}</label>
                <input
                  type="number"

                  value={collectForm.amount}
                  onChange={(e) => setCollectForm({ ...collectForm, amount: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 6 }}
                />
              </div>
              <div>
                <label style={{ color: 'white', fontSize: 14, display: 'block', marginBottom: 6, direction: dir, textAlign: textAlign }}>{t('notesReceivable.collect.collectionDate', { defaultValue: 'تاريخ التحصيل' })}</label>
                <input
                  type="date"
                  value={collectForm.collection_date}
                  onChange={(e) => setCollectForm({ ...collectForm, collection_date: e.target.value })}
                  style={{ width: '100%', padding: '10px', background: '#1a1f2e', color: 'white', border: '1px solid #333', borderRadius: 6 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <button onClick={() => setCollectId(null)} style={{ background: '#666', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign: textAlign }}>{t('notesReceivable.collect.cancel', { defaultValue: 'إلغاء' })}</button>
              <button
                onClick={async () => {
                  if (!collectForm.bank_account_id || !collectForm.amount || !collectForm.collection_date) {
                    toast.error(t('notesReceivable.collect.requiredFields', { defaultValue: 'برجاء استكمال بيانات التحصيل' }), { rtl: isRTL }); return;
                  }
                  try {
                    await dispatch(collectNoteReceivable({ id: collectId, data: { bank_account_id: Number(collectForm.bank_account_id), amount: Number(collectForm.amount), collection_date: collectForm.collection_date } })).unwrap();
                    setCollectId(null);
                    setCollectForm({ bank_account_id: '', amount: '', collection_date: '' });
                    dispatch(getNotesReceivable({ page: currentPage, per_page: perPage, search: searchTerm || undefined }));
                  } catch { }
                }}
                style={{ background: '#AC2000', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign: textAlign }}
              >
                {t('notesReceivable.collect.confirm', { defaultValue: 'تأكيد التحصيل' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {cancelId && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#1b3350', border: '1px solid #333', borderRadius: 12, padding: 20, width: 380, maxWidth: '95%' }}>
            <h3 style={{ color: 'white', margin: '0 0 12px', fontSize: '18px', direction: dir, textAlign: textAlign }}>{t('notesReceivable.cancel.title', { defaultValue: 'تأكيد إلغاء التحصيل' })}</h3>
            <p style={{ color: '#ccc', direction: dir, textAlign: textAlign }}>{t('notesReceivable.cancel.message', { defaultValue: 'هل أنت متأكد من إلغاء تحصيل ورقة القبض؟' })}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              <button onClick={() => setCancelId(null)} style={{ background: '#666', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign: textAlign }}>{t('notesReceivable.cancel.cancel', { defaultValue: 'رجوع' })}</button>
              <button
                onClick={async () => {
                  try {
                    await dispatch(cancelNoteReceivable(cancelId)).unwrap();
                    setCancelId(null);
                    dispatch(getNotesReceivable({ page: currentPage, per_page: perPage, search: searchTerm || undefined }));
                  } catch { }
                }}
                style={{ background: '#dc3545', color: 'white', border: 'none', padding: '8px 12px', borderRadius: 6, direction: dir, textAlign: textAlign }}
              >
                {t('notesReceivable.cancel.confirm', { defaultValue: 'تأكيد الإلغاء' })}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Print Template for Single Note Receivable */}
      {noteForPrint && pdfType === 'single' && (
        <div
          id={`note-receivable-pdf-${noteForPrint.id}`}
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0,
            width: '794px',
            minHeight: '1123px',
            backgroundColor: '#ffffff',
            color: '#000000',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            padding: '40px',
            boxSizing: 'border-box',
          }}
        >
          <div style={{
            border: '5px solid #000',
            padding: '3px',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            minHeight: '1043px',
          }}>
            <div style={{
              border: '2px solid #000',
              minHeight: '1033px',
              height: '1033px',
              padding: '18px 18px 10px',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: 'bold',
              direction: isRTL ? 'rtl' : 'ltr',
              backgroundColor: '#ffffff',
            }}>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundImage: `url(${headerLogo})`,
                backgroundSize: '95%',
                backgroundPosition: 'center center',
                backgroundRepeat: 'no-repeat',
                opacity: 0.08,
                zIndex: 0,
                pointerEvents: 'none'
              }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    marginBottom: '8px',
                    borderBottom: '4px double #000',
                    paddingBottom: '8px',
                    flexShrink: 0
                  }}
                  crossOrigin="anonymous"
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}</th>
                          <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{noteForPrint.customer_name || (isRTL ? (noteForPrint.customer?.name_ar || noteForPrint.customer?.name || '') : (noteForPrint.customer?.name_en || noteForPrint.customer?.name_ar || noteForPrint.customer?.name || '')) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{noteForPrint.project_name || (isRTL ? (noteForPrint.project?.name_ar || noteForPrint.project?.name || '') : (noteForPrint.project?.name_en || noteForPrint.project?.name_ar || noteForPrint.project?.name || '')) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{convertArabicNumbersToEnglish(noteForPrint.note_number) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(noteForPrint.amount) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(noteForPrint.issue_date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(noteForPrint.due_date) || '—'}</td>
                          <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{getStatusText(noteForPrint.status) || '—'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '10px',
                  left: '18px',
                  right: '18px',
                  flexShrink: 0
                }}>
                  <img
                    src={`${publicUrl}/FooterAce.png`}
                    alt="Footer"
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'contain',
                      borderTop: '4px double #000',
                      paddingTop: '8px',
                      flexShrink: 0
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Print Templates for All Notes Receivable - Multiple pages */}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIndex) => (
        <AllNotesReceivablePrintTemplate
          key={chunkIndex}
          notesReceivable={chunk.items || chunk}
          startIndex={chunk.startIndex ?? chunkIndex * 25}
          chunkIndex={chunkIndex}
          totalChunks={pdfChunks.length}
          pagination={pagination}
          isRTL={isRTL}
          t={t}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          getStatusText={getStatusText}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
        />
      ))}

      {/* Preview Modal */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && !!previewNote}
        title={previewNote?.all ? t('notesReceivable.title', { defaultValue: 'أوراق القبض' }) : t('notesReceivable.preview.title', { defaultValue: 'معاينة ورقة قبض' })}
        onClose={() => {
          setIsPreviewOpen(false);
          setPreviewNote(null);
          setPdfChunks([]);
        }}
        onPrint={() => {
          setIsPreviewOpen(false);
          if (previewNote?.all) {
            handlePrintAll();
          } else if (previewNote) {
            handlePrintNoteReceivable(previewNote);
          }
        }}
        isGeneratingPdf={isGeneratingPdf}
      >
        {previewNote?.all ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center', padding: '20px' }}>
            {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIdx) => (
              <AllNotesReceivablePreviewTemplate
                key={chunkIdx}
                notesReceivable={chunk.items || chunk}
                startIndex={chunk.startIndex ?? chunkIdx * 25}
                chunkIndex={chunkIdx}
                totalChunks={pdfChunks.length}
                isRTL={isRTL}
                t={t}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusText={getStatusText}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
              />
            ))}
          </div>
        ) : previewNote && !previewNote.all && (
          <SingleNoteReceivablePreviewTemplate
            note={previewNote}
            isRTL={isRTL}
            t={t}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            getStatusText={getStatusText}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
          />
        )}
      </PdfPreviewModal>

      {/* Measurement template for dynamic PDF pagination */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', pointerEvents: 'none', visibility: 'hidden' }}>
        <div id="notes-receivable-pdf-measure-top-header" style={{ width: '100%' }}>
          <img src={`${publicUrl}/HeaderAce.png`} alt="" style={{ width: '95%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px' }} />
          <h1 style={{ textAlign: 'center', fontSize: '18px', margin: '8px 0' }}>{t('notesReceivable.title')}</h1>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000' }}>
          <thead id="notes-receivable-pdf-measure-thead">
            <tr>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', width: '36px' }}>#</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.customer')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.project')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.noteNumber')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.amount')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.issueDate')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.dueDate')}</th>
              <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{t('notesReceivable.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody id="notes-receivable-pdf-measure-tbody">
            {items.slice(0, 5).map((note, idx) => (
              <tr key={idx}>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{idx + 1}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.customer_name || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.project_name || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.note_number || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.amount || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.issue_date || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.due_date || '—'}</td>
                <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>{note.status || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div id="notes-receivable-pdf-measure-footer" style={{ height: '120px' }} />
      </div>
    </div>
  );
};

// Single Note Receivable Preview Template
const SingleNoteReceivablePreviewTemplate = ({ note, isRTL, t, formatDate, formatCurrency, getStatusText, publicUrl, headerLogo }) => {
  if (!note) return null;

  return (
    <div
      id={`preview-note-receivable-pdf-${note.id}`}
      style={{
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        height: '1043px',
      }}>
        <div style={{
          border: '2px solid #000',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            />
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.customer_name || (isRTL ? (note.customer?.name_ar || note.customer?.name || '') : (note.customer?.name_en || note.customer?.name_ar || note.customer?.name || '')) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.project_name || (isRTL ? (note.project?.name_ar || note.project?.name || '') : (note.project?.name_en || note.project?.name_ar || note.project?.name || '')) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.note_number || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(note.amount) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.issue_date) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.due_date) || '—'}</td>
                      <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{getStatusText(note.status) || '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// All Notes Receivable Preview Template
const AllNotesReceivablePreviewTemplate = ({ notesReceivable, chunkIndex, startIndex = 0, totalChunks, isRTL, t, formatDate, formatCurrency, getStatusText, publicUrl, headerLogo }) => {
  if (!notesReceivable || notesReceivable.length === 0) return null;
  const isFirstPage = chunkIndex === 0;
  const isLastPage = chunkIndex === totalChunks - 1;

  return (
    <div
      id={`preview-notes-receivable-pdf-all-${chunkIndex}`}
      style={{
        marginBottom: chunkIndex < totalChunks - 1 ? '20px' : 0,
        width: '794px',
        height: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        height: '1043px',
        overflow: 'visible'
      }}>
        <div style={{
          border: '2px solid #000',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
          boxSizing: 'border-box',
          width: '100%',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            {isFirstPage && (
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            )}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif', flex: 1, overflow: 'hidden' }}>
                <h1 style={{ textAlign: "center", fontFamily: '"Times New Roman", "Times", serif', fontSize: '18px' }}>{t('notesReceivable.title')}</h1>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.serialNumber', { defaultValue: '#' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notesReceivable.map((note, index) => (
                      <tr key={note.id || index} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{startIndex + index + 1}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.customer_name || (isRTL ? (note.customer?.name_ar || note.customer?.name || '') : (note.customer?.name_en || note.customer?.name_ar || note.customer?.name || '')) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.project_name || (isRTL ? (note.project?.name_ar || note.project?.name || '') : (note.project?.name_en || note.project?.name_ar || note.project?.name || '')) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.note_number || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(note.amount) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.issue_date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.due_date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{getStatusText(note.status) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {isLastPage && (
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '18px',
                right: '18px',
                flexShrink: 0
              }}>
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderTop: '4px double #000',
                    paddingTop: '8px',
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Print Template for All Notes Receivable
const AllNotesReceivablePrintTemplate = ({ notesReceivable, chunkIndex, startIndex = 0, totalChunks, pagination, isRTL, t, formatDate, formatCurrency, getStatusText, publicUrl, headerLogo }) => {
  if (!notesReceivable || notesReceivable.length === 0) return null;
  const isFirstPage = chunkIndex === 0;
  const isLastPage = chunkIndex === totalChunks - 1;

  return (
    <div
      id={`notes-receivable-pdf-all-${chunkIndex}`}
      aria-hidden="true"
      style={{
        position: 'absolute',
        left: '-9999px',
        top: 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
        overflow: 'visible'
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        height: '1043px',
      }}>
        <div style={{
          border: '2px solid #000',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
            {isFirstPage && (
              <img
                src={`${publicUrl}/HeaderAce.png`}
                alt="Header"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  marginBottom: '8px',
                  borderBottom: '4px double #000',
                  paddingBottom: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            )}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif' }}>
                <h1 style={{ textAlign: "center", fontFamily: '"Times New Roman", "Times", serif', fontSize: '18px' }}>{t('notesReceivable.title')}</h1>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.serialNumber', { defaultValue: '#' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.customer', { defaultValue: 'العميل' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.project', { defaultValue: 'المشروع' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.noteNumber', { defaultValue: 'رقم الورقة' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.amount', { defaultValue: 'القيمة' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.issueDate', { defaultValue: 'تاريخ الإصدار' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.dueDate', { defaultValue: 'تاريخ الاستحقاق' })}</th>
                      <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', fontWeight: '700', backgroundColor: 'rgba(238, 242, 247, 0.7)' }}>{t('notesReceivable.tableHeaders.status', { defaultValue: 'الحالة' })}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notesReceivable.map((note, index) => (
                      <tr key={note.id || index} style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{startIndex + index + 1}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.customer_name || (isRTL ? (note.customer?.name_ar || note.customer?.name || '') : (note.customer?.name_en || note.customer?.name_ar || note.customer?.name || '')) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{note.project_name || (isRTL ? (note.project?.name_ar || note.project?.name || '') : (note.project?.name_en || note.project?.name_ar || note.project?.name || '')) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{convertArabicNumbersToEnglish(note.note_number) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatCurrency(note.amount) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.issue_date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{formatDate(note.due_date) || '—'}</td>
                        <td style={{ border: '1px solid #000', padding: '6px', textAlign: 'center', backgroundColor: 'rgba(255, 255, 255, 0.7)' }}>{getStatusText(note.status) || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {isLastPage && (
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '18px',
                right: '18px',
                flexShrink: 0
              }}>
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    borderTop: '4px double #000',
                    paddingTop: '8px',
                  }}
                  crossOrigin="anonymous"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotesReceivable;
